import { Ability } from '@casl/ability';
import { useGetTimeZones } from '@dispatch/shared/hooks';
import { useCurrentUser } from '@dispatch/web/auth';
import { ResultStatus, Actions, Subjects } from '@dispatch/shared/types';
import {
  CheckBoxField,
  DateTimeField,
  Section,
  SelectField,
  TextField,
} from '@dispatch/web/ui';
import {
  Button,
  Grid,
  Stack,
  InputAdornment,
  Toolbar,
  Box,
  FormHelperText,
} from '@mui/material';
import { useState } from 'react';
import { Control, FieldErrors, FieldValues } from 'react-hook-form';

export type EditScheduleProps = {
  control: Control<FieldValues>;
  errors: FieldErrors;
  onSubmit: () => void;
  upsertScheduleStatus: ResultStatus;
  isEdit?: boolean;
  permissions: Ability<[Actions, Subjects]> | undefined;
};

export const EditSchedule = ({
  control,
  errors,
  onSubmit,
  upsertScheduleStatus,
  isEdit,
  permissions,
}: EditScheduleProps) => {
  const [repeat, setRepeat] = useState(false);

  const hasEditSchedulePermission = permissions?.can?.(
    'upsert',
    'resource-schedules'
  );
  const { tenantId } = useCurrentUser();
  const { timeZones } = useGetTimeZones(tenantId || '');

  const renderDays = (days: { label: string; name: string }[]) =>
    days.map((day: { label: string; name: string }) => {
      return (
        <Grid size={1}>
          <div hidden={!repeat}>
            <CheckBoxField
              control={control}
              id={day.name}
              name={`days.${day.name}`}
              label={day.label}
              data-testid={day}
            />
          </div>
        </Grid>
      );
    });

  return (
    <div
      style={{
        padding: '24px',
      }}
    >
      <Section>
        <Stack>
          <Grid container spacing={2} marginBottom={2} alignItems="end">
            <Grid size={3}>
              <DateTimeField
                id="startDateTime"
                name="startDateTime"
                label="Start Date Time"
                control={control}
                errors={errors}
                required
              />
              <DateTimeField
                id="endDateTime"
                name="endDateTime"
                label="End Date Time"
                control={control}
                errors={errors}
                required
              />
              <SelectField
                id={'timeZoneId'}
                name={'timeZoneId'}
                label={'Schedule Time Zone'}
                control={control}
                options={timeZones}
                errors={errors}
                required
              />
              <CheckBoxField
                id="repeat"
                name="repeat"
                label="Repeat"
                control={control}
                checked={(field) => {
                  setRepeat(field.value);
                  return typeof field.value === 'boolean' ? field.value : false;
                }}
                disabled={isEdit}
              />
            </Grid>
            <Grid
              container
              spacing={0}
              size={{
                xs: 2,
                md: 12,
              }}
            >
              <div hidden={!repeat}>
                <TextField
                  control={control}
                  id="repeatFor"
                  name="repeatFor"
                  label="Repeat For the Next"
                  type="number"
                  inputProps={{
                    inputmode: 'numeric',
                    pattern: '[0-9]*',
                    endAdornment: (
                      <InputAdornment position="end">Days</InputAdornment>
                    ),
                  }}
                  errors={errors}
                  required
                />
              </div>
            </Grid>
            {renderDays([
              { label: 'Su', name: 'sunday' },
              { label: 'M', name: 'monday' },
              { label: 'Tu', name: 'tuesday' },
              { label: 'W', name: 'wednesday' },
              { label: 'Th', name: 'thursday' },
              { label: 'F', name: 'friday' },
              { label: 'Sa', name: 'saturday' },
            ])}
            <Grid
              hidden={!(repeat && errors && errors['days']?.message)}
              size={{
                xs: 3,
                md: 12,
              }}
            >
              <FormHelperText error={true}>
                {errors && errors['days']?.message}
              </FormHelperText>
            </Grid>
          </Grid>
          <Toolbar>
            <Box sx={{ flexGrow: 1 }} />
            <Box hidden={!hasEditSchedulePermission}>
              <Button
                variant="contained"
                color="info"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  lineHeight: '22px',
                  color: 'white',
                  letterSpacing: '0.46px',
                }}
                onClick={onSubmit}
                data-testid={'edit-schedule-save'}
              >
                {upsertScheduleStatus === 'loading' ? 'Saving' : 'Save'}
              </Button>
            </Box>
          </Toolbar>
        </Stack>
      </Section>
    </div>
  );
};
